import { DateTime } from "luxon";

/**
 * IANA time-zone helpers. Pure (luxon only — no IO/React/SQL).
 *
 * Storage keeps every timestamp as a true UTC instant. A `tz` (IANA name) does
 * NOT change what is stored; it changes how a recurring series is *expanded*
 * (so a 09:00 event stays 09:00 local across DST) and how the task form
 * interprets typed wall-clock.
 *
 * The bridge between the two worlds is a "floating" Date: a JS Date whose **UTC
 * fields hold the wall-clock components in `tz`**. rrule expands against this
 * floating representation (cadence is computed on stable wall-clock), and each
 * resulting occurrence is then re-anchored to a true UTC instant via the zone's
 * offset at that wall time.
 */

/** The device's IANA zone, e.g. "America/New_York". */
export function systemTimeZone(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
}

/**
 * UTC instant → floating Date (UTC fields = the wall-clock in `tz`).
 * e.g. 13:00Z in "America/New_York" (EST) → a Date whose getUTCHours() === 8.
 */
export function instantToFloating(iso: string, tz: string): Date {
  const dt = DateTime.fromISO(iso, { zone: "utc" }).setZone(tz);
  return new Date(Date.UTC(dt.year, dt.month - 1, dt.day, dt.hour, dt.minute, dt.second, dt.millisecond));
}

/**
 * Floating Date (UTC fields = wall-clock in `tz`) → true UTC instant ISO.
 * Luxon resolves DST edges: a nonexistent spring-forward time is bumped
 * forward, an ambiguous fall-back time takes the earlier offset.
 */
export function floatingToInstant(floating: Date, tz: string): string {
  const dt = DateTime.fromObject(
    {
      year: floating.getUTCFullYear(),
      month: floating.getUTCMonth() + 1,
      day: floating.getUTCDate(),
      hour: floating.getUTCHours(),
      minute: floating.getUTCMinutes(),
      second: floating.getUTCSeconds(),
      millisecond: floating.getUTCMilliseconds(),
    },
    { zone: tz },
  );
  return new Date(dt.toMillis()).toISOString();
}
